import * as React from 'react';
import {
  AgencyDownlineRepository,
  AgencyPersonalTokenRepository,
  UserPersonalTokenRepository,
} from '../../MedicareProAPI';
import type { Agency } from '../../MedicareProAPI/Entities/Agency/Agency';
import type { AgencyDownline } from '../../MedicareProAPI/Entities/Agency/AgencyDownline';
import StyledModal from './StyledModal';

interface AgencyHealthSherpaMedicareSetupProps {
  userAccessToken: string;
  userId: number;
  agencyId: number;
  thisAgency: Agency;
  thisHSFM?: number;
}

type CnxUI = { enabled: boolean; id?: number; username: string; pending?: boolean };

/**
 * Panel for setting up agency HealthSherpa Medicare — with paginated downlines
 *
 * @component
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @date Created: 2025-10-30
 * @lastUpdated 2025-10-30
 * @version 1.2.0 (adds pagination + fixes)
 */
export const AgencyHealthSherpaMedicareSetup = ({
  userAccessToken,
  userId,
  agencyId,
  thisAgency,
  thisHSFM,
}: AgencyHealthSherpaMedicareSetupProps): React.JSX.Element => {
  const lastFetchedOffsetRef = React.useRef<number>(-1);
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);

  const [hsTokenInput, setHsTokenInput] = React.useState<string>('');
  const [healthSherpaAgencyId, setHealthSherpaAgencyId] = React.useState<number | null>(thisHSFM);
  const [downlines, setDownlines] = React.useState<AgencyDownline[]>([]);

  const PAGE_SIZE = 50;

  const [offset, setOffset] = React.useState<number>(0);
  const [hasMore, setHasMore] = React.useState<boolean>(true);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [loadingMore, setLoadingMore] = React.useState<boolean>(false);
  const [cnxLocal, setCnxLocal] = React.useState<any>({});

  const [showHsModal, setShowHsModal] = React.useState(false);
  const [manage, setManage] = React.useState<boolean>(false);
  const [isSaving, setIsSaving] = React.useState<boolean>(false);

  const agencyDownlineRepository = React.useMemo(() => {
    const r = new AgencyDownlineRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const agencyPersonalTokenRepository = React.useMemo(() => {
    const r = new AgencyPersonalTokenRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const userPersonalTokenRepository = React.useMemo(() => {
    const r = new UserPersonalTokenRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const activeDownlineRows = React.useMemo(() => {
    const keep = new Set(['A', 'T']);
    return downlines.filter((d) => keep.has(d.getUser().getUserStatus()));
  }, [downlines]);

  /**
   * Fetch a page of downlines (offset/limit) and merge into local state.
   *
   * @param startOffset - Zero-based offset for the page to load
   * @param initial - If true, replaces the list; otherwise appends
   * @returns Promise<void>
   */
  const fetchDownlines = React.useCallback(
    async (startOffset: number, initial = false): Promise<void> => {
      // prevent duplicate fetch for the same offset
      if (loadingMore || startOffset === lastFetchedOffsetRef.current) return;

      setLoadingMore(true);
      lastFetchedOffsetRef.current = startOffset;

      try {
        const opts: any = {
          include: ['personalTokens'],
          limit: PAGE_SIZE,
          offset: startOffset,
          sortBy: 'userId',
        };
        const res: any = await agencyDownlineRepository.getAgencyDownlines(userId, agencyId, opts);

        const items: AgencyDownline[] = res.getItems();

        if (initial) setDownlines(items);
        else if (items.length) setDownlines((prev) => prev.concat(items));

        const total: number = res.getTotal();

        // if no items came back, stop trying further pages
        if (items.length === 0) {
          setHasMore(false);
          return;
        }

        const next = startOffset + items.length;
        setOffset(next);
        setHasMore(next < total);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [agencyDownlineRepository, agencyId, userId, loadingMore]
  );

  /**
   * Load the next page of downlines if available.
   *
   * @returns void
   */
  const onLoadMore = React.useCallback((): void => {
    if (!hasMore || loadingMore) return;
    void fetchDownlines(offset, false);
  }, [hasMore, loadingMore, offset, fetchDownlines]);

  /**
   * Attach an infinite-scroll observer to the sentinel element.
   *
   * @dependency onLoadMore
   */
  React.useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries, obs) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          obs.unobserve(entry.target);
          onLoadMore();
          // re-observe after the microtask to avoid rapid retriggers
          queueMicrotask(() => el && obs.observe(el));
        }
      },
      { rootMargin: '1200px' }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [onLoadMore]);

  /**
   * Reset pagination and load the first page when agency/user changes.
   *
   * @dependency agencyId, userId
   */
  React.useEffect(() => {
    setDownlines([]);
    setOffset(0);
    setHasMore(true);
    setLoading(true);
    setLoadingMore(false);
    lastFetchedOffsetRef.current = -1;

    void fetchDownlines(0, true);
  }, [agencyId, userId]);

  /**
   * Derive HS row UI state by combining server token and local edits.
   *
   * @param d - Agency downline row
   * @returns CnxUI with enabled flag, token id, and username
   */
  const getRowState = (d: AgencyDownline): CnxUI => {
    const u = d.getUser();
    const key = u.getUserId();
    const server: any = getUserHsToken(d);

    const serverId = server?.getId?.() ?? server?.id;
    const serverUsername = server?.getUsername?.() ?? server?.username ?? '';

    const base: CnxUI = {
      enabled: !!server,
      id: serverId,
      username: serverUsername,
    };

    const local = cnxLocal[key];
    return local ? { ...base, ...local } : base;
  };

  /**
   * Open the HS token modal and clear the input.
   */
  const openHsModal = (): void => {
    setHsTokenInput('');
    setShowHsModal(true);
  };

  /**
   * Create the agency-level HS token and update local UI state.
   *
   * @returns Promise<void>
   */
  const saveHsToken = async (): Promise<void> => {
    setIsSaving(true);
    if (!hsTokenInput.trim()) return;
    const payload = {
      external: true,
      personalAccessToken: hsTokenInput.trim(),
      name: 'HealthSherpaMedicare',
    };
    const res = await agencyPersonalTokenRepository.createAgencyPersonalToken(
      agencyId,
      'healthsherpamedicare',
      payload
    );
    setIsSaving(false);
    setHealthSherpaAgencyId(res.getId());
    setShowHsModal(false);
  };

  /**
   * Revoke the agency-level HS token after confirmation and update UI state.
   *
   * @returns Promise<void>
   */
  const revokeHsToken = async (): Promise<void> => {
    const r = window.confirm(
      'All downline agents will lose access to this integration. Are you sure you want to revoke this connection?'
    );
    if (!r) {
      return;
    }
    await agencyPersonalTokenRepository.deleteAgencyPersonalToken(agencyId, 'healthsherpamedicare');
    setHealthSherpaAgencyId(null);

    activeDownlineRows.forEach((d) => {
      const u = d.getUser();
      const st = getRowState(d);

      if (!st.enabled) return;

      userPersonalTokenRepository.deleteUserPersonalToken(u.getUserId(), 'healthsherpamedicare');
    });
  };

  /**
   * Optimistically toggle HS token access for a downline user and sync with the server.
   *
   * @param d - Agency downline row for the user
   * @param checked - Desired toggle state (true = enable, false = disable)
   * @returns Promise<void>
   */
  const toggleHsForUser = async (d: AgencyDownline, checked: boolean): Promise<void> => {
    const u = d.getUser();
    const key = u.getUserId();

    if (checked) {
      if (!healthSherpaAgencyId) return;
      setCnxLocal((m: any) => ({
        ...m,
        [key]: { ...(m[key] || {}), enabled: true, pending: true },
      }));
      try {
        const payload = {
          agencyPersonalAccessToken: healthSherpaAgencyId,
          name: 'HealthSherpaMedicare',
          external: true,
        };
        const resp = await userPersonalTokenRepository.createUserPersonalToken(
          u.getUserId(),
          'healthsherpamedicare',
          payload
        );
        const newId = resp?.getId?.();
        setCnxLocal((m: any) => ({
          ...m,
          [key]: { ...(m[key] || {}), enabled: true, id: newId, pending: false },
        }));
      } catch (e) {
        console.error(e);
        setCnxLocal((m: any) => ({
          ...m,
          [key]: { ...(m[key] || {}), enabled: false, id: undefined, pending: false },
        }));
      }
    } else {
      const server: any = getUserHsToken(d);
      const id = cnxLocal[key]?.id ?? server?.getId?.() ?? server?.id;
      if (!id) {
        setCnxLocal((m: any) => ({
          ...m,
          [key]: { enabled: false, id: undefined, pending: false },
        }));
        return;
      }
      try {
        await userPersonalTokenRepository.deleteUserPersonalToken(
          u.getUserId(),
          'healthsherpamedicare'
        );
      } catch (e) {
        console.error(e);
      } finally {
        setCnxLocal((m: any) => ({
          ...m,
          [key]: { enabled: false, id: undefined, pending: false, username: '' },
        }));
      }
    }
  };

  /**
   * Update a user's HS token username (optimistic UI).
   *
   * @param d - Agency downline row for the user
   * @param username - New username to set
   * @returns Promise<void>
   */
  const saveHsUsername = async (d: AgencyDownline, username: string): Promise<void> => {
    const u = d.getUser();
    const key = u.getUserId();
    const server: any = getUserHsToken(d);
    const id = cnxLocal[key]?.id ?? server?.getId?.() ?? server?.id;
    if (!id || !username) return;

    // optimistic update
    setCnxLocal((m: any) => ({ ...m, [key]: { ...(m[key] || {}), username } }));
    try {
      await userPersonalTokenRepository.updateUserPersonalToken(
        u.getUserId(),
        'healthsherpamedicare',
        id,
        { username: username.toLowerCase() }
      );
    } catch (e) {
      console.error(e);
    }
  };

  /**
   * Retrieve the HealthSherpa (Medicare) personal token for a downline user.
   *
   * @param d - Agency downline row
   * @returns The matching token entity, or undefined if none exists
   */
  const getUserHsToken = (d: AgencyDownline) =>
    d
      .getUser()
      .getUserPersonalTokens()
      .getItems()
      .find((t: any) => (t.getName?.() ?? t.name) === 'HealthSherpaMedicare');

  return (
    <>
      <div className="col-12 px-2 sm-resp" id="contact">
        <div className="card cardAddress">
          <div className="card-body mt-0" id="rxCards">
            <div className="row">
              <div className="col-12">
                <img
                  className="rounded ml-2 mt-3"
                  height={25}
                  src="https://medicareprocrm-cdn.s3.us-east-2.amazonaws.com/public/img/site/healthsherpamedicare.png"
                  alt="HealthSherpa Medicare"
                />
              </div>

              <div className="col-12 mt-3 mb-5">
                <button
                  type="button"
                  className="btn border-primary text-primary btn-sm p-2 d-inline-block cp px-3 hover-shadow"
                  style={{ borderRadius: 15, background: 'none' }}
                  onClick={() => setManage(!manage)}
                >
                  <b>Manage</b>
                </button>
              </div>

              {manage && (
                <>
                  {/* LEFT: Account Data */}
                  <div className="col-6 border-right">
                    <div className="mb-2">
                      <b>Account Data</b>
                    </div>

                    {healthSherpaAgencyId ? (
                      <>
                        <span className="badge badge-success mr-2 p-2" style={{ fontSize: '11pt' }}>
                          <i className="far fa-check-circle mr-2" />
                          Connected
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={revokeHsToken}
                        >
                          Revoke
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={openHsModal}
                      >
                        Add HealthSherpa API Key
                      </button>
                    )}
                  </div>

                  {/* RIGHT: Broker Access */}
                  <div className="col-lg-6 col-12">
                    <div className="mb-2">
                      <b>Broker Access</b>
                    </div>

                    {healthSherpaAgencyId && (
                      <>
                        <div className="alert alert-sm alert-secondary py-1 mb-3">
                          <i className="fal fa-comment-exclamation mr-2" />
                          Enable/disable downline agent access to HSFM. If disabled, the broker will
                          not see a HSFM enrollment option from a client profile page.
                        </div>

                        <div
                          className="border rounded"
                          style={{ maxHeight: 477, overflowY: 'auto' }}
                        >
                          {activeDownlineRows.map((d) => {
                            const u = d.getUser();
                            const st = getRowState(d);
                            return (
                              <div
                                key={`cnx-${u.getUserId()}`}
                                className="row border-bottom pb-1 pt-1 mx-0"
                              >
                                <div className="col-12 font-weight-bold">
                                  {u.getUserFirstname()} {u.getUserLastname()}
                                </div>

                                <div className="col-3 p-2 ml-1">
                                  <label className="switch mb-0">
                                    <input
                                      type="checkbox"
                                      checked={st.enabled}
                                      onChange={(e) => toggleHsForUser(d, e.target.checked)}
                                    />
                                    <span className="slider round pt-1" />
                                  </label>
                                  {st.pending && (
                                    <span className="ml-2 small text-muted">
                                      <i className="fal fa-spinner fa-spin" /> linking…
                                    </span>
                                  )}
                                </div>

                                <div className="col-6 form-inline">
                                  <input
                                    disabled={!st.enabled || st.pending || !st.id}
                                    placeholder="HS Username"
                                    className="form-control form-control-sm w-100"
                                    value={st.username}
                                    onChange={(e) =>
                                      setCnxLocal((m: any) => ({
                                        ...m,
                                        [u.getUserId()]: {
                                          ...(m[u.getUserId()] || {}),
                                          username: e.target.value,
                                        },
                                      }))
                                    }
                                  />
                                </div>

                                <div className="col-2">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-success d-inline-block"
                                    disabled={!st.enabled || st.pending || !st.id || !st.username}
                                    onClick={() => saveHsUsername(d, st.username)}
                                  >
                                    Save
                                  </button>
                                </div>
                              </div>
                            );
                          })}

                          {activeDownlineRows.length === 0 && !loading && (
                            <div className="p-3 text-muted">No brokers found.</div>
                          )}

                          {loadingMore && (
                            <div className="p-2 text-center">
                              <i className="fal fa-spinner fa-spin" /> Loading…
                            </div>
                          )}

                          {!loadingMore && hasMore && (
                            <div className="p-2 text-center">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={onLoadMore}
                              >
                                Load more
                              </button>
                            </div>
                          )}

                          <div ref={sentinelRef} />
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <StyledModal
        size="md"
        title="Select Organization"
        footerCloseButton
        show={showHsModal}
        onClose={() => setShowHsModal(false)}
      >
        <label className="label">Paste your API Key</label>
        <textarea
          className="form-control"
          rows={3}
          value={hsTokenInput}
          onChange={(e) => setHsTokenInput(e.target.value)}
        />
        <button
          type="button"
          className="btn btn-sm btn-success mt-2"
          onClick={saveHsToken}
          disabled={isSaving || !hsTokenInput?.trim()}
        >
          {isSaving ? 'saving...' : 'Save'}
        </button>
      </StyledModal>
    </>
  );
};
